import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { v4 } from "uuid";
import {
  resolveMenuItemId,
  resolveOriginalInPrice,
  resolveOriginalOutPrice,
  resolveOriginalPrice
} from "@/lib/comboMealPriceUtils";
import { calculateMixAndMatchPrice } from "@/lib/mixAndMatchPriceUtils";
import { isSameCourseItem } from "@/lib/banquetPriceUtils";



const helperAddOrSyncFreeItems = (state, parentCartItemId, data, quantity) => {
  const offers = (data?.variant?.free_item_offers && data.variant.free_item_offers.length > 0)
    ? data.variant.free_item_offers
    : ((data?.item?.free_item_offers && data.item.free_item_offers.length > 0) ? data.item.free_item_offers : []);

  if (!offers || offers.length === 0) return;

  offers.forEach(offer => {
    const reqQty = Number(offer.quantity) || 1;
    const freeQty = Math.floor(quantity / reqQty);

    if (Array.isArray(offer?.free_items)) {
      offer.free_items.forEach(freeItem => {
        // Find existing free item index in state
        const existingIndex = state.findIndex(
          cartItem => cartItem.parentCartItemId === parentCartItemId && cartItem.freeItemId === freeItem.id && cartItem.offerId === offer.offer_id
        );

        if (freeQty > 0) {
          if (existingIndex !== -1) {
            state[existingIndex].quantity = freeQty;
          } else {
            const freeVariant = (freeItem?.variants && freeItem.variants.length > 0) ? freeItem.variants[0] : null;
            const menuItemId = freeItem?.menu_item_id || freeVariant?.menu_item_id || (freeItem?.variants && freeItem.variants[0]?.menu_item_id) || data?.item?.menu_item_id || null;
            state.push({
              id: v4(),
              parentCartItemId: parentCartItemId,
              isFreeItem: true,
              offerId: offer.offer_id,
              offerName: offer.offer_name || offer.name || "Free Offer",
              freeItemId: freeItem.id,
              quantity: freeQty,
              data: {
                item: {
                  ...freeItem,
                  menu_item_id: menuItemId,
                  price: 0,
                  in_price: 0,
                  out_price: 0,
                  discount_price: 0
                },
                variant: freeVariant ? {
                  ...freeVariant,
                  in_price: 0,
                  out_price: 0
                } : null
              },
              extraAddon: [],
              modifiers: [],
              extraToppings: [],
              extraSauces: [],
              extraSalads: [],
              buffetPriceFor: "Adult",
              selectedCourseItem: []
            });
          }
        } else {
          // If free item quantity is 0 (or qualified quantity drops below requirement), remove it
          if (existingIndex !== -1) {
            state.splice(existingIndex, 1);
          }
        }
      });
    }
  });
};

const findCourseItemTarget = (cartItem, { courseId, courseItemIndex, courseItemId }) => {
  if (!cartItem) return null;

  // 1. Check inside cartItem.selectedCourseItem (Choose Your Own & Banquet)
  if (Array.isArray(cartItem.selectedCourseItem)) {
    const coursesToSearch = (courseId !== undefined && courseId !== null)
      ? cartItem.selectedCourseItem.filter(c => String(c.id) === String(courseId))
      : cartItem.selectedCourseItem;

    for (const course of coursesToSearch) {
      if (Array.isArray(course.course_items)) {
        if (courseItemId) {
          const found = course.course_items.find(
            ci => ci.course_item_unique_id === courseItemId ||
                  ci.unique_id === courseItemId ||
                  String(ci.id) === String(courseItemId) ||
                  (ci.item_id && String(ci.item_id) === String(courseItemId))
          );
          if (found) return found;
        }

        if (courseItemIndex !== undefined && courseItemIndex !== null && course.course_items[courseItemIndex]) {
          if ((courseId !== undefined && courseId !== null) || cartItem.selectedCourseItem.length === 1) {
            return course.course_items[courseItemIndex];
          }
        }
      }
    }
  }

  // 2. Check inside cartItem.data?.item?.course_items (standard course meals)
  if (cartItem.data?.item?.course_items && Array.isArray(cartItem.data.item.course_items)) {
    if (courseItemId) {
      const found = cartItem.data.item.course_items.find(
        ci => ci.course_item_unique_id === courseItemId ||
              ci.unique_id === courseItemId ||
              String(ci.id) === String(courseItemId) ||
              (ci.item_id && String(ci.item_id) === String(courseItemId))
      );
      if (found) return found;
    }
    if (courseItemIndex !== undefined && courseItemIndex !== null && cartItem.data.item.course_items[courseItemIndex]) {
      return cartItem.data.item.course_items[courseItemIndex];
    }
  }

  return null;
};

const cartSlice = createSlice({
  name: "cartSlice",
  initialState: [],
  reducers: {
    addItem: (state, action) => {
        const item = state.find(item => item.id === action.payload.id);
        if (item) {
            item.quantity = action.payload.quantity;
            if (action.payload?.selectedCourseItem) {
              item.selectedCourseItem = action.payload.selectedCourseItem;
            }
            if (action.payload?.selectedMixAndMatchItems) {
              item.selectedMixAndMatchItems = action.payload.selectedMixAndMatchItems;
            }
            if (action.payload?.extraAddon) {
              item.extraAddon = action.payload.extraAddon;
            }
            if (action.payload?.modifiers) {
              item.modifiers = action.payload.modifiers;
            }
            if (action.payload?.extraToppings) {
              item.extraToppings = action.payload.extraToppings;
            }
            if (action.payload?.extraSauces) {
              item.extraSauces = action.payload.extraSauces;
            }
            if (action.payload?.extraSalads) {
              item.extraSalads = action.payload.extraSalads;
            }
            if (action.payload?.unitPrice !== undefined) {
              item.unitPrice = action.payload.unitPrice;
            }
            if (action.payload?.isCustomPizza) {
              item.isCustomPizza = true;
              item.customPizzaData = action.payload.customPizzaData;
              item.data = action.payload.data;
              item.unitPrice = action.payload.unitPrice;
            }
        } else {
            let data = action.payload?.data;
            if (data?.item?.combo_items && Array.isArray(data.item.combo_items)) {
                data = {
                    ...data,
                    item: {
                        ...data.item,
                        combo_items: data.item.combo_items.map((ci) => {
                            const origInPrice = resolveOriginalInPrice(ci);
                            const origOutPrice = resolveOriginalOutPrice(ci);
                            const origPrice = resolveOriginalPrice(ci);
                            const itemMenuItemId = ci.menu_item_id ? Number(ci.menu_item_id) : resolveMenuItemId(ci, ci.item_type);
                            const originalMenuItemId = ci.original_item?.menu_item_id ? Number(ci.original_item.menu_item_id) : itemMenuItemId;
                            const originalItemType = ci.original_item?.item_type || ci.item_type || null;

                            return {
                                ...ci,
                                combo_item_unique_id: ci.combo_item_unique_id || v4(),
                                menu_item_id: itemMenuItemId,
                                item_type: ci.item_type || null,
                                original_in_price: origInPrice,
                                original_out_price: origOutPrice,
                                original_price: origPrice,
                                original_item: ci.original_item || {
                                    id: ci.item_id || ci.id,
                                    name: ci.menu_item?.name || ci.display_name || ci.name || (ci.item_type ? ci.item_type.replace(/_/g, ' ') : "Item"),
                                    display_name: ci.display_name || ci.name || ci.menu_item?.name || (ci.item_type ? ci.item_type.replace(/_/g, ' ') : "Item"),
                                    in_price: origInPrice,
                                    out_price: origOutPrice,
                                    price: origPrice,
                                    original_in_price: origInPrice,
                                    original_out_price: origOutPrice,
                                    original_price: origPrice,
                                    variant: ci.variant || null,
                                    variant_id: ci.variant_id || null,
                                    item_type: originalItemType,
                                    menu_item_id: originalMenuItemId,
                                    menu_item: ci.menu_item || null
                                },
                                is_replaced: Boolean(ci.is_replaced),
                                replacement_in_price: Number(ci.replacement_in_price || 0),
                                replacement_out_price: Number(ci.replacement_out_price || 0),
                                replacement_extra_in_price: Number(ci.replacement_extra_in_price || 0),
                                replacement_extra_out_price: Number(ci.replacement_extra_out_price || 0),
                                replacement_extra_price: Number(ci.replacement_extra_price || 0),
                                extraAddon: ci.extraAddon || [],
                                modifiers: ci.modifiers || [],
                                extraToppings: ci.extraToppings || [],
                                extraSauces: ci.extraSauces || [],
                                extraSalads: ci.extraSalads || [],
                                defaultAddons: ci.defaultAddons || ci.menu_item?.add_ons?.map(a => ({ ...a, canceled: false })) || [],
                                defaultSauces: ci.defaultSauces || ci.menu_item?.sauces?.map(s => ({ ...s, canceled: false })) || [],
                                defaultSalads: ci.defaultSalads || ci.menu_item?.salads?.map(s => ({ ...s, canceled: false })) || [],
                                defaultToppings: ci.defaultToppings || ci.menu_item?.toppings?.map(t => ({ ...t, canceled: false })) || []
                            };
                        })
                    }
                };
            }
            if (data?.item?.course_items && Array.isArray(data.item.course_items)) {
                data = {
                    ...data,
                    item: {
                        ...data.item,
                        course_items: data.item.course_items.map((ci) => {
                            const origInPrice = resolveOriginalInPrice(ci);
                            const origOutPrice = resolveOriginalOutPrice(ci);
                            const origPrice = resolveOriginalPrice(ci);
                            const itemMenuItemId = ci.menu_item_id ? Number(ci.menu_item_id) : resolveMenuItemId(ci, ci.item_type);
                            const originalMenuItemId = ci.original_item?.menu_item_id ? Number(ci.original_item.menu_item_id) : itemMenuItemId;
                            const originalItemType = ci.original_item?.item_type || ci.item_type || null;

                            return {
                                ...ci,
                                course_item_unique_id: ci.course_item_unique_id || ci.unique_id || v4(),
                                menu_item_id: itemMenuItemId,
                                item_type: ci.item_type || null,
                                original_in_price: origInPrice,
                                original_out_price: origOutPrice,
                                original_price: origPrice,
                                original_item: ci.original_item || {
                                    id: ci.item_id || ci.id,
                                    name: ci.menu_item?.name || ci.display_name || ci.name || (ci.item_type ? ci.item_type.replace(/_/g, ' ') : "Item"),
                                    display_name: ci.display_name || ci.name || ci.menu_item?.name || (ci.item_type ? ci.item_type.replace(/_/g, ' ') : "Item"),
                                    in_price: origInPrice,
                                    out_price: origOutPrice,
                                    price: origPrice,
                                    original_in_price: origInPrice,
                                    original_out_price: origOutPrice,
                                    original_price: origPrice,
                                    variant: ci.variant || null,
                                    variant_id: ci.variant_id || null,
                                    item_type: originalItemType,
                                    menu_item_id: originalMenuItemId,
                                    menu_item: ci.menu_item || null
                                },
                                is_replaced: Boolean(ci.is_replaced),
                                replacement_in_price: Number(ci.replacement_in_price || 0),
                                replacement_out_price: Number(ci.replacement_out_price || 0),
                                replacement_extra_in_price: Number(ci.replacement_extra_in_price || 0),
                                replacement_extra_out_price: Number(ci.replacement_extra_out_price || 0),
                                replacement_extra_price: Number(ci.replacement_extra_price || 0),
                                extraAddon: ci.extraAddon || [],
                                modifiers: ci.modifiers || [],
                                extraToppings: ci.extraToppings || [],
                                extraSauces: ci.extraSauces || [],
                                extraSalads: ci.extraSalads || [],
                                defaultAddons: ci.defaultAddons || ci.menu_item?.add_ons?.map(a => ({ ...a, canceled: false })) || [],
                                defaultSauces: ci.defaultSauces || ci.menu_item?.sauces?.map(s => ({ ...s, canceled: false })) || [],
                                defaultSalads: ci.defaultSalads || ci.menu_item?.salads?.map(s => ({ ...s, canceled: false })) || [],
                                defaultToppings: ci.defaultToppings || ci.menu_item?.toppings?.map(t => ({ ...t, canceled: false })) || []
                            };
                        })
                    }
                };
            }
            state.push({
              id: action.payload?.id,
              data: data,
              quantity: action.payload?.quantity,
              extraAddon: action.payload?.extraAddon || [],
              modifiers: action.payload?.modifiers || [],
              extraToppings: action.payload?.extraToppings || [],
              extraSauces: action.payload?.extraSauces || [],
              extraSalads: action.payload?.extraSalads || [],
              buffetPriceFor: action.payload?.buffetPriceFor || "Adult",
              selectedCourseItem: action.payload?.selectedCourseItem || [],
              selectedMixAndMatchItems: action.payload?.selectedMixAndMatchItems || [],
              unitPrice: action.payload?.unitPrice,
              isCustomPizza: action.payload?.isCustomPizza || false,
              customPizzaData: action.payload?.customPizzaData || null,
            });
        }
        helperAddOrSyncFreeItems(state, action.payload?.id, action.payload?.data || (item ? item.data : null), action.payload?.quantity);
  
        return state;
    },
    removeItem: (state, action) => {
      return state.filter(item => item.id !== action.payload && item.parentCartItemId !== action.payload);
    },

    cancelDefaultAddon: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.add_ons){
            item.data.item.add_ons = item.data.item.add_ons.map(addon => 
            addon.id === action.payload.addonId 
              ? {...addon, canceled: true}
              : addon
            );
        }
      }
      return state;
    },

    restoreDefaultAddon: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.add_ons){
            item.data.item.add_ons = item.data.item.add_ons.map(addon => 
            addon.id === action.payload.addonId 
              ? {...addon, canceled: false}
              : addon
            );
        }
      }
      return state;
    },

    addExtraAddons: (state, action) => {

      const item = state.find(item => item.id == action.payload.id);
      if(item && item.extraAddon){
        item.extraAddon = [...item.extraAddon, {id: v4(), addon:action.payload.addon}]
      }
      // state.extraAddon = [...state.extraAddon, action.payload]
      return state;

    },

    removeExtraAddon: (state, action) => {

      const item = state.find(item => item.id === action.payload.id);

      item.extraAddon = item.extraAddon.filter(item => item.id !== action.payload.addon)

      return state;
    },

    cancelDefaultTopping: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.toppings){
            item.data.item.toppings = item.data.item.toppings.map(topping => 
            topping.id === action.payload.toppingId 
              ? {...topping, canceled: true}
              : topping
            );
        }
      }
      return state;
    },

    restoreDefaultTopping: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.toppings){
            item.data.item.toppings = item.data.item.toppings.map(topping => 
            topping.id === action.payload.topingId 
              ? {...topping, canceled: false}
              : topping
            );
        }
      }
      return state;
    },

    addExtraToppings: (state, action) => {

      const item = state.find(item => item.id == action.payload.id);
      if(item && item.extraToppings){
        item.extraToppings = [...item.extraToppings, {id: v4(), topping:action.payload.topping}]
      }
      // state.extraToppings = [...state.extraToppings, action.payload]
      return state;

    },

    removeExtraToppings: (state, action) => {

      const item = state.find(item => item.id === action.payload.id);

      item.extraToppings = item.extraToppings.filter(item => item.id !== action.payload.topping)

      return state;
    },

    cancelDefaultSausage: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.sauces){
            item.data.item.sauces = item.data.item.sauces.map(sauce => 
            sauce.id === action.payload.sauceId 
              ? {...sauce, canceled: true}
              : sauce
            );
        }
      }
      return state;
    },

    restoreDefaultSausage: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.sauces){
            item.data.item.sauces = item.data.item.sauces.map(sauce => 
            sauce.id === action.payload.sauceId 
              ? {...sauce, canceled: false}
              : sauce
            );
        }
      }
      return state;
    },

    addExtraSauces: (state, action) => {

      const item = state.find(item => item.id == action.payload.id);
      if(item && item.extraSauces){
        item.extraSauces = [...item.extraSauces, {id: v4(), sauce:action.payload.sauce}]
      }
      // state.extraToppings = [...state.extraToppings, action.payload]
      return state;

    },

    removeExtraSauces: (state, action) => {

      const item = state.find(item => item.id === action.payload.id);

      item.extraSauces = item.extraSauces.filter(item => item.id !== action.payload.sauce)

      return state;
    },

    cancelDefaultSalad: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.salads){
            item.data.item.salads = item.data.item.salads.map(salad => 
            salad.id === action.payload.saladId 
              ? {...salad, canceled: true}
              : salad
            );
        }
      }
      return state;
    },

    restoreDefaultSalad: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        if(item?.data?.item?.salads){ 
            item.data.item.salads = item.data.item.salads.map(salad => 
            salad.id === action.payload.saladId 
              ? {...salad, canceled: false}
              : salad
            );
        }
      }
      return state;
    },

    addExtraSalads: (state, action) => {

      const item = state.find(item => item.id == action.payload.id);
      if(item && item.extraSalads){
        item.extraSalads = [...item.extraSalads, {id: v4(), salad:action.payload.salad}]
      }
      // state.extraToppings = [...state.extraToppings, action.payload]
      return state;

    },
    removeExtraSalads: (state, action) => {

      const item = state.find(item => item.id === action.payload.id);

      item.extraSalads = item.extraSalads.filter(item => item.id !== action.payload.salad)

      return state;
    },


    addModifier: (state, action) => {

      const item = state.find(item => item.id == action.payload.id);

      if(item && item.modifiers){
        item.modifiers = [...item.modifiers, {id: v4(), data:{...action.payload.data}}]
      }

      return state;

    },

    removeModifier: (state, action) => {

      const item = state.find(item => item.id === action.payload.id);

      item.modifiers = item.modifiers.filter(item => item.id !== action.payload.modifier)
      
      return state;
    },
    changeBuffetPriceFor: (state, action) => {

      const item = state.find(item => item.id === action.payload.id);

      item.buffetPriceFor = action.payload.priceFor;
      
      return state;
    },
    updateCourseItemFromCart: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        const definedCourses = item?.data?.item?.courses || [];
        if (!Array.isArray(item.selectedCourseItem)) {
          item.selectedCourseItem = [];
        }
        if (Array.isArray(definedCourses) && definedCourses.length > 0) {
          definedCourses.forEach(dc => {
            const exists = item.selectedCourseItem.some(c => String(c.id) === String(dc.id));
            if (!exists) {
              item.selectedCourseItem.push({
                ...dc,
                course_items: []
              });
            }
          });
        }
        item.selectedCourseItem = item.selectedCourseItem.map(course => {
          if(String(course?.id) === String(action.payload.courseId)){
            const isItemAlreadySelected = course?.course_items?.some(courseItem => 
              isSameCourseItem(courseItem, action.payload.item)
            );
            if(isItemAlreadySelected){
              // remove item from course
              course.course_items = course.course_items.filter(courseItem => 
                !isSameCourseItem(courseItem, action.payload.item)
              );
            }else{
              const rawItem = action.payload.item;
              const newItem = {
                ...rawItem,
                course_item_unique_id: rawItem.course_item_unique_id || rawItem.unique_id || v4(),
                extraAddon: rawItem.extraAddon || [],
                modifiers: rawItem.modifiers || [],
                extraToppings: rawItem.extraToppings || [],
                extraSauces: rawItem.extraSauces || [],
                extraSalads: rawItem.extraSalads || [],
                defaultAddons: rawItem.defaultAddons || rawItem.menu_item?.add_ons?.map(a => ({ ...a, canceled: false })) || [],
                defaultSauces: rawItem.defaultSauces || rawItem.menu_item?.sauces?.map(s => ({ ...s, canceled: false })) || [],
                defaultSalads: rawItem.defaultSalads || rawItem.menu_item?.salads?.map(s => ({ ...s, canceled: false })) || [],
                defaultToppings: rawItem.defaultToppings || rawItem.menu_item?.toppings?.map(t => ({ ...t, canceled: false })) || []
              };

              if (course.max_selections === 1 && course.course_items.length === 1) {
                course.course_items = [newItem];
              } else if (course.max_selections && course.course_items.length >= course.max_selections) {
                toast.error(`You can select up to ${course.max_selections} items for the course "${course.name}".`);
              } else {
                course.course_items = [...course.course_items, newItem];
              }
            }
          }
          return course;
        });
      }
      return state;
    },

    updateMixAndMatchItemFromCart: (state, action) => {
      const { cartItemId, selectedMixAndMatchItems, unitPrice, quantity } = action.payload;
      const item = state.find((it) => it.id === cartItemId);
      if (item) {
        if (selectedMixAndMatchItems) {
          item.selectedMixAndMatchItems = selectedMixAndMatchItems;
        }
        if (unitPrice !== undefined) {
          item.unitPrice = unitPrice;
          if (item.data?.item) {
            item.data.item.price = unitPrice;
            item.data.item.in_price = unitPrice;
            item.data.item.out_price = unitPrice;
          }
        }
        if (quantity !== undefined) {
          item.quantity = quantity;
          helperAddOrSyncFreeItems(state, cartItemId, item.data, quantity);
        }
      }
      return state;
    },
    updateMixAndMatchItemSelectionFromCart: (state, action) => {
      const { id, categoryId, categoryName, categoryPrice, item, delta = 1, maxChoose = 1 } = action.payload;
      const cartItem = state.find((it) => it.id === id);
      if (cartItem) {
        const itemId = item?.id !== undefined ? item?.id : (item?.name || item);
        const itemName = item?.name || item;
        let currentList = cartItem.selectedMixAndMatchItems ? [...cartItem.selectedMixAndMatchItems] : [];
        const totalSelected = currentList.reduce((sum, it) => sum + (Number(it.quantity) || 1), 0);
        const existingIndex = currentList.findIndex(
          (it) =>
            (String(it.category_id) === String(categoryId) || (it.category_name && categoryName && String(it.category_name).toLowerCase().trim() === String(categoryName).toLowerCase().trim())) &&
            (String(it.item_id) === String(itemId) || (it.item_name && itemName && String(it.item_name).toLowerCase().trim() === String(itemName).toLowerCase().trim()) || (it.name && itemName && String(it.name).toLowerCase().trim() === String(itemName).toLowerCase().trim()))
        );

        if (delta > 0) {
          if (totalSelected < maxChoose) {
            if (existingIndex !== -1) {
              currentList[existingIndex].quantity = (Number(currentList[existingIndex].quantity) || 1) + delta;
            } else {
              currentList.push({
                category_id: categoryId,
                category_name: categoryName,
                category_price: Number(categoryPrice) || 0,
                item_id: itemId,
                item_name: itemName,
                quantity: 1
              });
            }
          }
        } else if (delta < 0) {
          if (existingIndex !== -1) {
            const currentQty = Number(currentList[existingIndex].quantity) || 1;
            if (currentQty + delta <= 0) {
              currentList.splice(existingIndex, 1);
            } else {
              currentList[existingIndex].quantity = currentQty + delta;
            }
          }
        }
        cartItem.selectedMixAndMatchItems = currentList;
        const newUnitPrice = calculateMixAndMatchPrice(cartItem.data?.item, currentList);
        cartItem.unitPrice = newUnitPrice;
        if (cartItem.data?.item) {
          cartItem.data.item.price = newUnitPrice;
          cartItem.data.item.in_price = newUnitPrice;
          cartItem.data.item.out_price = newUnitPrice;
        }
      }
      return state;
    },
    updateItemQuantity: (state, action) => {
      const item = state.find(item => item.id === action.payload.id);
      if(item){
        item.quantity = action.payload.quantity;
        helperAddOrSyncFreeItems(state, action.payload.id, item.data, action.payload.quantity);
      }
      return state;
    },
    cleanCart: (state, action) => {
      state = [];
      return state;
    },
    setCart : (state, action) => {
      state = action.payload;
      return state;
    },

    updateComboItemModifications: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId, modifications } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target) {
          target.extraAddon = modifications.extraAddon || [];
          target.modifiers = modifications.modifiers || [];
          target.extraToppings = modifications.extraToppings || [];
          target.extraSauces = modifications.extraSauces || [];
          target.extraSalads = modifications.extraSalads || [];
          target.defaultAddons = modifications.defaultAddons || [];
          target.defaultSauces = modifications.defaultSauces || [];
          target.defaultSalads = modifications.defaultSalads || [];
          target.defaultToppings = modifications.defaultToppings || [];
        }
      }
      return state;
    },

    removeComboItemExtraAddon: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId, addonId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target && target.extraAddon) {
          target.extraAddon = target.extraAddon.filter(item => item.id !== addonId);
        }
      }
      return state;
    },

    removeComboItemModifier: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId, modifierId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target && target.modifiers) {
          target.modifiers = target.modifiers.filter(item => item.id !== modifierId);
        }
      }
      return state;
    },

    removeComboItemExtraTopping: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId, toppingId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target && target.extraToppings) {
          target.extraToppings = target.extraToppings.filter(item => item.id !== toppingId);
        }
      }
      return state;
    },

    removeComboItemExtraSauce: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId, sauceId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target && target.extraSauces) {
          target.extraSauces = target.extraSauces.filter(item => item.id !== sauceId);
        }
      }
      return state;
    },

    removeComboItemExtraSalad: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId, saladId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target && target.extraSalads) {
          target.extraSalads = target.extraSalads.filter(item => item.id !== saladId);
        }
      }
      return state;
    },

    toggleComboItemDefaultAddon: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId, addonId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target && target.defaultAddons) {
          target.defaultAddons = target.defaultAddons.map(addon =>
            addon.id === addonId ? { ...addon, canceled: !addon.canceled } : addon
          );
        }
      }
      return state;
    },

    replaceComboItem: (state, action) => {
      const {
        cartItemId,
        comboItemIndex,
        comboItemId,
        replacementItem,
        replacementVariant,
        replacement_in_price,
        replacement_out_price,
        replacement_extra_in_price,
        replacement_extra_out_price,
        replacementExtraPrice,
      } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target) {
          // Backup original item if not already backed up
          if (!target.original_item) {
            const origInPrice = resolveOriginalInPrice(target);
            const origOutPrice = resolveOriginalOutPrice(target);
            const origPrice = resolveOriginalPrice(target);
            const origMenuItemId = target.menu_item_id ? Number(target.menu_item_id) : resolveMenuItemId(target, target.item_type);
            const origItemType = target.item_type || null;

            target.original_item = {
              id: target.item_id || target.id,
              name: target.menu_item?.name || target.display_name || target.name || "Item",
              display_name: target.display_name || target.name || target.menu_item?.name || "Item",
              in_price: origInPrice,
              out_price: origOutPrice,
              price: origPrice,
              original_in_price: origInPrice,
              original_out_price: origOutPrice,
              original_price: origPrice,
              variant: target.variant || null,
              variant_id: target.variant_id || null,
              item_type: origItemType,
              menu_item_id: origMenuItemId,
              menu_item: target.menu_item || null,
              defaultAddons: target.defaultAddons || [],
              defaultSauces: target.defaultSauces || [],
              defaultSalads: target.defaultSalads || [],
              defaultToppings: target.defaultToppings || []
            };
            target.original_in_price = origInPrice;
            target.original_out_price = origOutPrice;
            target.original_price = origPrice;
          }

          const repMenuItemId = replacementItem?.menu_item_id ? Number(replacementItem.menu_item_id) : resolveMenuItemId(replacementItem, replacementItem?.menu_type || target.item_type);
          const repItemType = replacementItem?.item_type || replacementItem?.menu_type || target.item_type || null;

          // Update target to replacement item
          target.is_replaced = true;
          target.item_id = replacementItem.id;
          target.display_name = replacementItem.name;
          target.name = replacementItem.name;
          target.item_name = replacementItem.name;
          target.item_type = repItemType;
          target.menu_item_id = repMenuItemId;
          target.menu_item = replacementItem;
          target.variant = replacementVariant || null;
          target.variant_id = replacementVariant?.id || null;
          target.in_price = Number(replacement_in_price ?? replacementVariant?.in_price ?? replacementItem?.in_price ?? replacementItem?.price ?? 0);
          target.out_price = Number(replacement_out_price ?? replacementVariant?.out_price ?? replacementItem?.out_price ?? replacementItem?.price ?? 0);
          target.price = target.in_price;
          target.replacement_in_price = Number(replacement_in_price || 0);
          target.replacement_out_price = Number(replacement_out_price || 0);
          target.replacement_extra_in_price = Number(replacement_extra_in_price || 0);
          target.replacement_extra_out_price = Number(replacement_extra_out_price || 0);
          target.replacement_extra_price = Number(replacementExtraPrice || 0);

          // Reset extra modifications for the new item
          target.extraAddon = [];
          target.modifiers = [];
          target.extraToppings = [];
          target.extraSauces = [];
          target.extraSalads = [];
          // Initialize default addons / sauces / salads / toppings for the replacement item
          target.defaultAddons = replacementItem?.add_ons?.map(a => ({ ...a, canceled: false })) || [];
          target.defaultSauces = replacementItem?.sauces?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultSalads = replacementItem?.salads?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultToppings = replacementItem?.toppings?.map(t => ({ ...t, canceled: false })) || [];
        }
      }
      return state;
    },

    revertComboItemReplacement: (state, action) => {
      const { cartItemId, comboItemIndex, comboItemId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem && cartItem.data?.item?.combo_items) {
        const target = cartItem.data.item.combo_items.find(
          (ci, idx) => (comboItemId && (ci.combo_item_unique_id === comboItemId || ci.id === comboItemId)) || idx === comboItemIndex
        );
        if (target && target.original_item) {
          const orig = target.original_item;
          target.is_replaced = false;
          target.item_id = orig.id;
          target.display_name = orig.display_name || orig.name;
          target.name = orig.name;
          target.item_name = orig.name;
          target.item_type = orig.item_type;
          target.menu_item_id = orig.menu_item_id;
          target.menu_item = orig.menu_item;
          target.variant = orig.variant;
          target.variant_id = orig.variant_id;
          target.in_price = orig.in_price;
          target.out_price = orig.out_price;
          target.price = orig.price;
          target.original_in_price = orig.original_in_price ?? orig.in_price;
          target.original_out_price = orig.original_out_price ?? orig.out_price;
          target.original_price = orig.original_price ?? orig.price;
          target.replacement_in_price = 0;
          target.replacement_out_price = 0;
          target.replacement_extra_in_price = 0;
          target.replacement_extra_out_price = 0;
          target.replacement_extra_price = 0;

          // Reset modifications
          target.extraAddon = [];
          target.modifiers = [];
          target.extraToppings = [];
          target.extraSauces = [];
          target.extraSalads = [];
          target.defaultAddons = orig.defaultAddons || orig.menu_item?.add_ons?.map(a => ({ ...a, canceled: false })) || [];
          target.defaultSauces = orig.defaultSauces || orig.menu_item?.sauces?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultSalads = orig.defaultSalads || orig.menu_item?.salads?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultToppings = orig.defaultToppings || orig.menu_item?.toppings?.map(t => ({ ...t, canceled: false })) || [];
        }
      }
      return state;
    },

    updateCourseItemModifications: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId, modifications } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target) {
          target.extraAddon = modifications.extraAddon || [];
          target.modifiers = modifications.modifiers || [];
          target.extraToppings = modifications.extraToppings || [];
          target.extraSauces = modifications.extraSauces || [];
          target.extraSalads = modifications.extraSalads || [];
          target.defaultAddons = modifications.defaultAddons || [];
          target.defaultSauces = modifications.defaultSauces || [];
          target.defaultSalads = modifications.defaultSalads || [];
          target.defaultToppings = modifications.defaultToppings || [];
        }
      }
      return state;
    },

    removeCourseItemExtraAddon: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId, addonId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target && target.extraAddon) {
          target.extraAddon = target.extraAddon.filter(item => item.id !== addonId);
        }
      }
      return state;
    },

    removeCourseItemModifier: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId, modifierId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target && target.modifiers) {
          target.modifiers = target.modifiers.filter(item => item.id !== modifierId);
        }
      }
      return state;
    },

    removeCourseItemExtraTopping: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId, toppingId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target && target.extraToppings) {
          target.extraToppings = target.extraToppings.filter(item => item.id !== toppingId);
        }
      }
      return state;
    },

    removeCourseItemExtraSauce: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId, sauceId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target && target.extraSauces) {
          target.extraSauces = target.extraSauces.filter(item => item.id !== sauceId);
        }
      }
      return state;
    },

    removeCourseItemExtraSalad: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId, saladId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target && target.extraSalads) {
          target.extraSalads = target.extraSalads.filter(item => item.id !== saladId);
        }
      }
      return state;
    },

    toggleCourseItemDefaultAddon: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId, addonId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target && target.defaultAddons) {
          target.defaultAddons = target.defaultAddons.map(addon =>
            addon.id === addonId ? { ...addon, canceled: !addon.canceled } : addon
          );
        }
      }
      return state;
    },

    replaceCourseItem: (state, action) => {
      const {
        cartItemId,
        courseId,
        courseItemIndex,
        courseItemId,
        replacementItem,
        replacementVariant,
        replacement_in_price,
        replacement_out_price,
        replacement_extra_in_price,
        replacement_extra_out_price,
        replacementExtraPrice,
      } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target) {
          if (!target.original_item) {
            const origInPrice = resolveOriginalInPrice(target);
            const origOutPrice = resolveOriginalOutPrice(target);
            const origPrice = resolveOriginalPrice(target);
            const origMenuItemId = target.menu_item_id ? Number(target.menu_item_id) : resolveMenuItemId(target, target.item_type);
            const origItemType = target.item_type || null;

            target.original_item = {
              id: target.item_id || target.id,
              name: target.menu_item?.name || target.display_name || target.name || "Item",
              display_name: target.display_name || target.name || target.menu_item?.name || "Item",
              in_price: origInPrice,
              out_price: origOutPrice,
              price: origPrice,
              original_in_price: origInPrice,
              original_out_price: origOutPrice,
              original_price: origPrice,
              variant: target.variant || null,
              variant_id: target.variant_id || null,
              item_type: origItemType,
              menu_item_id: origMenuItemId,
              menu_item: target.menu_item || null,
              defaultAddons: target.defaultAddons || [],
              defaultSauces: target.defaultSauces || [],
              defaultSalads: target.defaultSalads || [],
              defaultToppings: target.defaultToppings || []
            };
            target.original_in_price = origInPrice;
            target.original_out_price = origOutPrice;
            target.original_price = origPrice;
          }

          const repMenuItemId = replacementItem?.menu_item_id ? Number(replacementItem.menu_item_id) : resolveMenuItemId(replacementItem, replacementItem?.menu_type || target.item_type);
          const repItemType = replacementItem?.item_type || replacementItem?.menu_type || target.item_type || null;

          target.is_replaced = true;
          target.item_id = replacementItem.id;
          target.display_name = replacementItem.name;
          target.name = replacementItem.name;
          target.item_name = replacementItem.name;
          target.item_type = repItemType;
          target.menu_item_id = repMenuItemId;
          target.menu_item = replacementItem;
          target.variant = replacementVariant || null;
          target.variant_id = replacementVariant?.id || null;
          target.in_price = Number(replacement_in_price ?? replacementVariant?.in_price ?? replacementItem?.in_price ?? replacementItem?.price ?? 0);
          target.out_price = Number(replacement_out_price ?? replacementVariant?.out_price ?? replacementItem?.out_price ?? replacementItem?.price ?? 0);
          target.price = target.in_price;
          target.replacement_in_price = Number(replacement_in_price || 0);
          target.replacement_out_price = Number(replacement_out_price || 0);
          target.replacement_extra_in_price = Number(replacement_extra_in_price || 0);
          target.replacement_extra_out_price = Number(replacement_extra_out_price || 0);
          target.replacement_extra_price = Number(replacementExtraPrice || 0);

          target.extraAddon = [];
          target.modifiers = [];
          target.extraToppings = [];
          target.extraSauces = [];
          target.extraSalads = [];
          target.defaultAddons = replacementItem?.add_ons?.map(a => ({ ...a, canceled: false })) || [];
          target.defaultSauces = replacementItem?.sauces?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultSalads = replacementItem?.salads?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultToppings = replacementItem?.toppings?.map(t => ({ ...t, canceled: false })) || [];
        }
      }
      return state;
    },

    revertCourseItemReplacement: (state, action) => {
      const { cartItemId, courseId, courseItemIndex, courseItemId } = action.payload;
      const cartItem = state.find(item => item.id === cartItemId);
      if (cartItem) {
        const target = findCourseItemTarget(cartItem, { courseId, courseItemIndex, courseItemId });
        if (target && target.original_item) {
          const orig = target.original_item;
          target.is_replaced = false;
          target.item_id = orig.id;
          target.display_name = orig.display_name || orig.name;
          target.name = orig.name;
          target.item_name = orig.name;
          target.item_type = orig.item_type;
          target.menu_item_id = orig.menu_item_id;
          target.menu_item = orig.menu_item;
          target.variant = orig.variant;
          target.variant_id = orig.variant_id;
          target.in_price = orig.in_price;
          target.out_price = orig.out_price;
          target.price = orig.price;
          target.original_in_price = orig.original_in_price ?? orig.in_price;
          target.original_out_price = orig.original_out_price ?? orig.out_price;
          target.original_price = orig.original_price ?? orig.price;
          target.replacement_in_price = 0;
          target.replacement_out_price = 0;
          target.replacement_extra_in_price = 0;
          target.replacement_extra_out_price = 0;
          target.replacement_extra_price = 0;

          target.extraAddon = [];
          target.modifiers = [];
          target.extraToppings = [];
          target.extraSauces = [];
          target.extraSalads = [];
          target.defaultAddons = orig.defaultAddons || orig.menu_item?.add_ons?.map(a => ({ ...a, canceled: false })) || [];
          target.defaultSauces = orig.defaultSauces || orig.menu_item?.sauces?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultSalads = orig.defaultSalads || orig.menu_item?.salads?.map(s => ({ ...s, canceled: false })) || [];
          target.defaultToppings = orig.defaultToppings || orig.menu_item?.toppings?.map(t => ({ ...t, canceled: false })) || [];
        }
      }
      return state;
    },

  }
});

export const { 
  addItem, 
  removeItem, 
  updateItemQuantity, 
  addExtraAddons, 
  removeExtraAddon, 
  addModifier, 
  removeModifier, 
  cancelDefaultAddon, 
  restoreDefaultAddon, 
  cancelDefaultTopping, 
  restoreDefaultTopping, 
  addExtraToppings, 
  removeExtraToppings, 
  cancelDefaultSausage, 
  restoreDefaultSausage, 
  addExtraSauces, 
  removeExtraSauces, 
  cancelDefaultSalad, 
  restoreDefaultSalad,
  addExtraSalads, 
  removeExtraSalads, 
  changeBuffetPriceFor, 
  updateCourseItemFromCart, 
  cleanCart, 
  setCart, 
  updateComboItemModifications, 
  removeComboItemExtraAddon, 
  removeComboItemModifier, 
  removeComboItemExtraTopping, 
  removeComboItemExtraSauce, 
  removeComboItemExtraSalad, 
  toggleComboItemDefaultAddon, 
  replaceComboItem, 
  revertComboItemReplacement,
  updateMixAndMatchItemFromCart,
  updateMixAndMatchItemSelectionFromCart,
  updateCourseItemModifications,
  removeCourseItemExtraAddon,
  removeCourseItemModifier,
  removeCourseItemExtraTopping,
  removeCourseItemExtraSauce,
  removeCourseItemExtraSalad,
  toggleCourseItemDefaultAddon,
  replaceCourseItem,
  revertCourseItemReplacement
} = cartSlice.actions;

export default cartSlice.reducer;
